import { LinearProgress, Tooltip } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { useRouter } from "@uirouter/react";
import BN from "bn.js";
import { round } from "lodash";
import moment from "moment";
import { FC } from "react";
import { Option } from "space-monad";
import styled from "styled-components";
import { PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { Flex } from "../../../components/layout";
import { WIDGET_BORDER_RADIUS } from "../../../components/widget";
import { colours } from "../../../styles/colours";
import { formatDecimal, formatEther, OptionType } from "../../../utils/utils";
import { StakingRoutes } from "../routes";
import { useStaking } from "../single/staking-hook";

const BorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 12,
    borderRadius: 7,
    width: "100%",
    marginRight: ".5em",
    border: `1px solid rgba(39,40,72, 0.2)`,
  },
  colorPrimary: {
    backgroundColor:
      theme.palette.grey[theme.palette.type === "light" ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: colours.blue,
  },
}))(LinearProgress);

const Arrow = styled(Icon).attrs({ type: "rightArrow" })`
  font-size: 2em;
`;

const Title = styled.div`
  font-size: 1.6em;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ListItem = styled.li`
  background: ${colours.lightBlue};
  border-radius: ${WIDGET_BORDER_RADIUS};

  ${Title} {
    color: ${colours.darkBlueGrey};
  }

  & + & {
    margin-top: 1em;
  }
`;

const Description = styled(Flex)`
  width: 100%;
  justify-content: space-between;

  & + & {
    margin-top: 1em;
  }
`;

const Head = styled(Description)`
  background: ${colours.darkBlueGrey};
  color: ${colours.white};
  border-radius: ${WIDGET_BORDER_RADIUS};
  margin-bottom: 1em;
  padding: 2em 1em;

  ${Title} {
    color: ${colours.white};
    font-weight: 500;
  }
`;

const Descriptions = styled.div`
  padding: 1em;
`;

export const MobileList: FC<{
  contracts: OptionType<ReturnType<typeof useStaking>["allContracts"]>;
}> = ({ contracts }) => {
  const router = useRouter();

  return (
    <List>
      {contracts.map((x) => (
        <ListItem
          key={x.address}
          onClick={() => {
            router.stateService.go(StakingRoutes.names.single, x);
          }}
        >
          <Head>
            <div>
              <Tooltip
                title={moment().add(x.stakingMaturityDays).format("DD-MM-YYYY")}
              >
                <Title>
                  {round(
                    moment()
                      .add(x.stakingMaturityDays, "days")
                      .diff(moment(), "months", true)
                  )}{" "}
                  months
                </Title>
              </Tooltip>
              <div>
                {round(
                  moment()
                    .add(x.lockedPeriodDays, "days")
                    .diff(moment(), "months", true)
                )}{" "}
                months locked
              </div>
            </div>
            <Title>
              {x.rewardPercentageBreakdown.rewardAnnualAdopterPercentage}% -{" "}
              {x.rewardPercentageBreakdown.rewardAnnualPartnerPercentage}%
            </Title>
            <PrimaryButton>
              <Arrow />
            </PrimaryButton>
          </Head>
          <Descriptions>
            <Description>
              <div>Total Locked Tokens</div>
              <Title>
                <small>$SKRT</small>{" "}
                {formatDecimal(new BN(x.totalStakedTokens), 0)}
              </Title>
            </Description>
            <Description>
              <div>Number of Participants</div>
              <Title>{x.numberOfStakers}</Title>
            </Description>
            <Description>
              <div>Your Locked</div>
              <Title>
                {Option(x)
                  .map((x) => x.walletInfo)
                  .map((x) => x.balance)
                  .map((x) => formatEther(x))
                  .map((x) => `$SKRT ${x}`)
                  .getOrElse("n/a")}
              </Title>
            </Description>
            <Description>
              <div>Capacity</div>
              <Title>
                <small>
                  {formatDecimal(new BN(x.totalStakedTokens), 0)} /
                  {formatDecimal(new BN(x.stakingPoolSize), 0)} $SKRT
                </small>
              </Title>
            </Description>
            <Description>
              <BorderLinearProgress
                value={(x.totalStakedTokens / x.stakingPoolSize) * 100}
                variant="determinate"
              />
              <div style={{ whiteSpace: "nowrap" }}>
                {round((x.totalStakedTokens / x.stakingPoolSize) * 100)} %
              </div>
            </Description>
          </Descriptions>
        </ListItem>
      ))}
    </List>
  );
};
